import React from 'react';
import Button from '../ui/Button';
import { StarIcon, CheckCircleIcon, MessageCircleIcon, ArrowDownIcon } from 'lucide-react';
import YouTubeEmbed from '../ui/YouTubeEmbed';
import { trackContact } from '../tracking/metaPixel';
import { useLanguage } from '../../context/LanguageContext';
import { getWhatsAppLink } from '../../utils/whatsappLink';
const Hero = () => {
  const {
    t,
    language
  } = useLanguage();
  const YOUTUBE_VIDEO_ID = 'K8RA16_6ZoU';
  const handleCtaClick = () => {
    trackContact({
      location: 'hero_primary',
      language,
      action: 'scroll_to_form' // Keep scrolling to form as primary action
    });
  };
  return <section className="py-12 md:py-20 px-4 bg-premium-gradient text-[#F9FAFB] relative overflow-hidden" data-id="element-70" data-mp="2mxg65laamgze">
      {/* Background Pattern Overlay */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] pointer-events-none" data-id="element-71" data-mp="3fny5qcbt6082"></div>

      <div className="container mx-auto relative z-10" data-id="element-72" data-mp="3kg96tiiabs27">
        <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center" data-id="element-73" data-mp="2kamvb1qpqctz">
          {/* Content Column */}
          <div className="w-full md:w-1/2 space-y-6 text-center md:text-left" data-id="element-74" data-mp="2gb48g0fbzroi">
            {/* Social Proof Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 mx-auto md:mx-0" data-id="element-75" data-mp="2u2ismakz5yx5">
              <div className="flex" data-id="element-76" data-mp="33yvuvwul5l3i">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#FFD700] fill-[#FFD700]" data-id="element-77" data-mp="2z0kltoc47emc" data-mpi={i} />)}
              </div>
              <span className="text-sm font-medium text-gray-200" data-id="element-78" data-mp="3cxporfgj7xdd">
                {t.hero.badge}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl leading-tight" data-id="element-79" data-mp="1imkmaeh6lqzm">
              <span className="text-accent block mb-2 text-2xl md:text-3xl uppercase tracking-wider" data-id="element-80" data-mp="1doskmsqqiqi8">
                {t.hero.headlineAccent}
              </span>
              {t.hero.headline}
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-gray-300 max-w-xl mx-auto md:mx-0 leading-relaxed" data-id="element-81" data-mp="3nmviux0r4ays">
              {t.hero.subheadline}
            </p>

            {/* Benefits List (Mobile optimized) */}
            <div className="flex flex-col gap-3 max-w-sm mx-auto md:mx-0 text-left bg-white/5 p-4 rounded-xl border border-white/10" data-id="element-82" data-mp="j75cpldq8yu">
              {t.hero.benefits.map((benefit, index) => <div key={index} className="flex items-start gap-3" data-id="element-83" data-mp="a7ywivim8p8p" data-mpi={index}>
                  <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" data-id="element-84" data-mp="aauv8m2afhy7" />
                  <span className="text-gray-200 text-sm md:text-base" data-id="element-85" data-mp="f9o6e7u3qwv7">
                    {benefit}
                  </span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center md:justify-start items-center" data-id="element-86" data-mp="o5x6lopwlzie"></div>

            <p className="text-xs text-gray-400 text-center md:text-left" data-id="element-87" data-mp="23rhc10n2h61u">
              {t.hero.response}
            </p>
          </div>

          {/* Video Column */}
          <div className="w-full md:w-1/2 relative" data-id="element-88" data-mp="1zrqf0f6y5tov">
            <div className="absolute -inset-4 bg-accent/20 rounded-full blur-3xl opacity-30 animate-pulse" data-id="element-89" data-mp="1urayjerdc62o"></div>

            {/* Video Container */}
            <div className="relative" data-id="element-90" data-mp="2rimqpch9tsak">
              <div className="bg-white p-2 rounded-2xl shadow-2xl" data-id="element-91" data-mp="2neyhyqgwkqnq">
                <YouTubeEmbed videoId={YOUTUBE_VIDEO_ID} className="rounded-xl aspect-[9/16] md:aspect-video" data-id="element-92" data-mp="2ioi4x7gzv71w" />
              </div>
            </div>

            {/* Testimonial Badge — Below video, no overlap */}
            <div className="mt-4 flex justify-center md:justify-end" data-id="element-93" data-mp="3b6c9wvb56k86">
              <div className="bg-white text-dark px-5 py-3 rounded-xl shadow-lg border-l-4 border-accent inline-flex items-center gap-3" data-id="element-94" data-mp="36dt0majhmb96">
                <div data-id="element-95" data-mp="2px8qonzzul32">
                  <p className="font-bold text-sm md:text-base leading-tight" data-id="element-96" data-mp="2uxmws0i1bkyg">
                    "{t.hero.videoBadge}"
                  </p>
                  <div className="flex items-center gap-2 mt-1" data-id="element-97" data-mp="2gyx0gn31hpb5">
                    <div className="flex" data-id="element-98" data-mp="2lwoq1ag7ozc6">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3 h-3 text-accent fill-accent" data-id="element-99" data-mp="39mswdpksbd6v" data-mpi={i} />)}
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-100" data-mp="2zx9824eayr4m">
                      Pedro de la Cruz
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce text-white/50 hidden md:block" data-id="element-101" data-mp="16r0nc6uykbg">
        <ArrowDownIcon className="w-6 h-6" data-id="element-102" data-mp="akrfy1nywixu" />
      </div>
    </section>;
};
export default Hero;